import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Download,
  Loader2,
  CheckCircle2,
  FileSpreadsheet,
  FileText,
  FileCode2,
} from "lucide-react";
import { AGENT_COLORS } from "../utils/tools";
import { agents_icons } from "./agentsIcons";

const DEFAULT_ACCENT = "#EC10A3";
const COMPLETED_GLOW = "#7A8BFF";

const FILE_TYPE_META = {
  excel: { label: "Excel", icon: FileSpreadsheet },
  xlsx: { label: "Excel", icon: FileSpreadsheet },
  csv: { label: "CSV", icon: FileSpreadsheet },
  pdf: { label: "PDF", icon: FileText },
  markdown: { label: "Markdown", icon: FileCode2 },
  file: { label: "File", icon: FileText },
};

function getAccent(agentName) {
  if (!agentName) return DEFAULT_ACCENT;
  return AGENT_COLORS[agentName] || DEFAULT_ACCENT;
}

function hexToRgbChannels(hex = "") {
  const match = hex.trim().match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!match) return "236,16,163";
  let value = match[1];
  if (value.length === 3) {
    value = value
      .split("")
      .map((char) => char + char)
      .join("");
  }
  const intValue = parseInt(value, 16);
  const r = (intValue >> 16) & 255;
  const g = (intValue >> 8) & 255;
  const b = intValue & 255;
  return `${r},${g},${b}`;
}

function createTaskFromEvent(evt) {
  const timestamp =
    typeof evt.timestamp === "number" ? evt.timestamp : Date.now();
  return {
    taskKey: evt.taskKey || evt.toolSlug || evt.id,
    status: evt.status || "running",
    summary: evt.summary || "",
    files: Array.isArray(evt.files) ? evt.files : [],
    durationSeconds:
      typeof evt.durationSeconds === "number" ? evt.durationSeconds : null,
    durationMinutes:
      typeof evt.durationMinutes === "number" ? evt.durationMinutes : null,
    firstTimestamp: timestamp,
    lastUpdated: timestamp,
  };
}

function applyEventToTask(task, evt) {
  task.status = evt.status || task.status;
  if (evt.summary) task.summary = evt.summary;
  if (Array.isArray(evt.files) && evt.files.length) task.files = evt.files;
  if (Number.isFinite(evt.durationSeconds)) task.durationSeconds = evt.durationSeconds;
  if (Number.isFinite(evt.durationMinutes)) task.durationMinutes = evt.durationMinutes;
  if (typeof evt.timestamp === "number") {
    task.firstTimestamp = Math.min(task.firstTimestamp, evt.timestamp);
    task.lastUpdated = Math.max(task.lastUpdated, evt.timestamp);
  }
}

function deriveAgentCards(events = []) {
  if (!Array.isArray(events) || events.length === 0) return [];
  const order = [];
  const map = new Map();

  events.forEach((evt) => {
    if (!evt) return;
    const cardKey = evt.agentSlug || evt.agentName || evt.toolSlug || evt.id;
    if (!cardKey) return;
    if (!map.has(cardKey)) {
      map.set(cardKey, {
        key: cardKey,
        agentName: evt.agentName || "Agent",
        agentSlug: evt.agentSlug || null,
        tasks: [],
        tasksByKey: new Map(),
      });
      order.push(cardKey);
    }
    const card = map.get(cardKey);
    card.agentName = evt.agentName || card.agentName;
    const taskKey = evt.taskKey || evt.toolSlug || evt.id;
    if (!taskKey) return;
    let task = card.tasksByKey.get(taskKey);
    if (!task) {
      task = createTaskFromEvent(evt);
      card.tasksByKey.set(taskKey, task);
      card.tasks.push(task);
    } else {
      applyEventToTask(task, evt);
    }
  });

  return order.map((key) => {
    const card = map.get(key);
    card.tasks.sort((a, b) => a.firstTimestamp - b.firstTimestamp);
    card.tasks.forEach((task, idx) => {
      task.label = task.label || `Task ${idx + 1}`;
    });
    const latestTask = card.tasks.reduce((latest, task) => {
      if (!latest) return task;
      return task.lastUpdated > latest.lastUpdated ? task : latest;
    }, null);
    const defaultTaskKey = latestTask?.taskKey || card.tasks[0]?.taskKey || null;
    const cardStatus = card.tasks.some((t) => t.status === "running")
      ? "running"
      : card.tasks.every((t) => t.status === "completed")
      ? "completed"
      : "info";
    return {
      key: card.key,
      agentName: card.agentName,
      agentSlug: card.agentSlug,
      status: cardStatus,
      tasks: card.tasks,
      defaultTaskKey,
    };
  });
}

function formatDuration(task = {}) {
  if (task.durationMinutes && task.durationMinutes >= 1) {
    const mins = Math.max(1, Math.floor(task.durationMinutes));
    return `${mins} min${mins === 1 ? "" : "s"}`;
  }
  if (task.durationSeconds && task.durationSeconds >= 1) {
    const secs = Math.max(1, Math.floor(task.durationSeconds));
    return `${secs} sec${secs === 1 ? "" : "s"}`;
  }
  return "—";
}

function extractSummarySections(summary = "") {
  if (!summary || typeof summary !== "string")
    return { body: "", sources: [] };

  const sourcesMatch = summary.match(/(^|\n)\s*\*?\*?Sources:?\*?\*?\s*([\s\S]*)$/i);
  if (!sourcesMatch) {
    return { body: summary.trim(), sources: [] };
  }

  const body = summary.slice(0, sourcesMatch.index).trim();
  const sourcesText = (sourcesMatch[2] || "").trim();
  const sources = sourcesText
    .split(/\n+/)
    .map((line) => line.replace(/^[-•]\s*/, "").trim())
    .filter(Boolean);

  return { body, sources };
}

function parseMarkdownBlocks(text = "") {
  if (!text) return [];
  const lines = text.split(/\r?\n/);
  const blocks = [];
  let paragraphBuffer = [];
  let currentList = null;

  const flushParagraph = () => {
    if (paragraphBuffer.length) {
      const paragraph = paragraphBuffer.join(" ").trim();
      if (paragraph) blocks.push({ type: "paragraph", text: paragraph });
      paragraphBuffer = [];
    }
  };

  const flushList = () => {
    if (currentList && currentList.length) {
      blocks.push({ type: "list", items: currentList });
    }
    currentList = null;
  };

  lines.forEach((line) => {
    const listMatch = line.match(/^\s*[-*]\s+/);
    const trimmed = line.trim();

    if (listMatch) {
      flushParagraph();
      if (!currentList) currentList = [];
      const item = line.replace(/^\s*[-*]\s+/, "").trim();
      if (item) currentList.push(item);
      return;
    }

    if (!trimmed) {
      flushParagraph();
      flushList();
      return;
    }

    flushList();
    paragraphBuffer.push(trimmed);
  });

  flushParagraph();
  flushList();

  return blocks;
}

const SUMMARY_COLLAPSED_HEIGHT = 300;

function formatSourceLabel(href = "") {
  if (!href) return "";
  try {
    const url = new URL(href.trim());
    return (url.hostname || href).replace(/^www\./i, "");
  } catch {
    return href
      .replace(/^https?:\/\//i, "")
      .split("/")[0]
      .replace(/^www\./i, "")
      .trim();
  }
}

function highlightContent(text, color) {
  if (!text) return null;
  const parts = [];
  let i = 0;

  while (i < text.length) {
    if (text[i] === "*" && text[i + 1] === "*") {
      const endIdx = text.indexOf("**", i + 2);
      if (endIdx !== -1) {
        parts.push({ type: "bold", value: text.slice(i + 2, endIdx) });
        i = endIdx + 2;
        continue;
      }
    }

    if (text[i] === "*" && text[i + 1] !== "*") {
      const endIdx = text.indexOf("*", i + 1);
      if (endIdx !== -1) {
        parts.push({ type: "italic", value: text.slice(i + 1, endIdx) });
        i = endIdx + 1;
        continue;
      }
    }

    const percentMatch = text.slice(i).match(/^[~≈]?[\d.,]+%/);
    if (percentMatch) {
      parts.push({ type: "percentage", value: percentMatch[0] });
      i += percentMatch[0].length;
      continue;
    }

    const plantMatch = text.slice(i).match(/^(PH|Plant\s+)?[A-Z]{2}\d{2}/i);
    if (plantMatch) {
      parts.push({ type: "plant", value: plantMatch[0].trim() });
      i += plantMatch[0].length;
      continue;
    }

    const numericMatch = text
      .slice(i)
      .match(/^[a-zA-Z0-9_$.,~≈\-/]*\d[a-zA-Z0-9_$.,~≈\-/]*/);
    if (numericMatch && numericMatch[0]) {
      parts.push({ type: "numeric", value: numericMatch[0] });
      i += numericMatch[0].length;
      continue;
    }

    const unitMatch = text.slice(i).match(/^(USD\/?kg|USD|kg)/i);
    if (unitMatch) {
      parts.push({ type: "unit", value: unitMatch[0] });
      i += unitMatch[0].length;
      continue;
    }

    parts.push({ type: "text", value: text[i] });
    i += 1;
  }

  const chipStyle = (bg) => ({
    fontFamily: "monospace",
    fontWeight: 600,
    color,
    background: bg,
    borderRadius: 6,
    padding: "2px 6px",
    margin: "0 2px",
    boxShadow: "none",
  });

  return parts.map((token, idx) => {
    if (token.type === "text") {
      return (
        <span key={`txt_${idx}`} style={{ whiteSpace: "pre-wrap" }}>
          {token.value}
        </span>
      );
    }
    if (token.type === "bold") {
      return (
        <span
          key={`bold_${idx}`}
          style={{
            fontWeight: 600,
            color: "#ffffff",
            backgroundColor: "rgba(255,255,255,0.08)",
            borderRadius: 6,
            padding: "0 6px",
            margin: "0 2px",
          }}
        >
          {token.value}
        </span>
      );
    }
    if (token.type === "italic") {
      return (
        <span
          key={`italic_${idx}`}
          style={{
            fontStyle: "italic",
            color: "#e5e7eb",
          }}
        >
          {highlightContent(token.value, color)}
        </span>
      );
    }
    if (token.type === "percentage") {
      return (
        <span
          key={`pct_${idx}`}
          style={chipStyle(
            `linear-gradient(135deg, ${color}35, rgba(255,255,255,0.05))`
          )}
        >
          {token.value}
        </span>
      );
    }
    if (token.type === "numeric") {
      return (
        <span
          key={`num_${idx}`}
          style={chipStyle(
            `linear-gradient(135deg, ${color}25, transparent)`
          )}
        >
          {token.value}
        </span>
      );
    }
    if (token.type === "plant") {
      return (
        <span
          key={`plant_${idx}`}
          style={chipStyle(
            `linear-gradient(135deg, ${color}30, rgba(255,255,255,0.06))`
          )}
        >
          {token.value}
        </span>
      );
    }
    if (token.type === "unit") {
      return (
        <span
          key={`unit_${idx}`}
          style={chipStyle(
            `linear-gradient(135deg, rgba(255,255,255,0.05), ${color}20)`
          )}
        >
          {token.value}
        </span>
      );
    }
    return null;
  });
}


function FileButtons({ files = [], color }) {
  if (!files.length) return null;

  const handleDownload = (url) => {
    try {
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      // no-op
    }
  };

  return (
    <div style={{ display: "grid", gap: 10 }}>
      {files.map((file) => {
        const type = FILE_TYPE_META[file.type] || FILE_TYPE_META.file;
        const TypeIcon = type.icon;
        return (
          <button
            key={`${file.url}_${file.name}`}
            onClick={() => handleDownload(file.url)}
            style={{
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.06)",
              background: "rgba(255,255,255,0.02)",
              color: "#e5e7eb",
              fontSize: 13,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 14px",
              cursor: "pointer",
              transition: "background 0.2s ease, border-color 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.05)";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.02)";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)";
            }}
          >
            <span style={{ fontSize: 18, display: "flex" }}>
              {TypeIcon ? <TypeIcon size={18} color={color} /> : ""}
            </span>
            <span style={{ flex: 1, textAlign: "left" }}>{file.name}</span>
            <span
              style={{
                fontSize: 12,
                color: "#cbd5f5",
                border: "1px solid rgba(203,213,245,0.35)",
                borderRadius: 999,
                padding: "2px 8px",
              }}
            >
              {type.label}
            </span>
            <Download size={16} color={color} />
          </button>
        );
      })}
    </div>
  );
}

function AgentCard({ card, expanded, onToggle, index }) {
  const color = getAccent(card.agentName);
  const tasks = Array.isArray(card.tasks) ? card.tasks : [];
  const [activeTaskKey, setActiveTaskKey] = useState(card.defaultTaskKey);
  useEffect(() => {
    setActiveTaskKey(card.defaultTaskKey);
  }, [card.defaultTaskKey, card.key, tasks.length]);
  const activeTask =
    tasks.find((task) => task.taskKey === activeTaskKey) || tasks[0] || {};
  const durationLabel = formatDuration(activeTask);
  const { body: summaryBody, sources } = useMemo(
    () => extractSummarySections(activeTask.summary),
    [activeTask.summary]
  );
  const summaryBlocks = useMemo(
    () => parseMarkdownBlocks(summaryBody),
    [summaryBody]
  );
  const hasSummary = summaryBlocks.length > 0;
  const hasSources = sources.length > 0;
  const hasContent = hasSummary || hasSources;
  const showLoader = (activeTask.status || card.status) !== "completed";
  const [showAll, setShowAll] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const summaryRef = useRef(null);

  useEffect(() => {
    setShowAll(false);
  }, [summaryBody]);

  useEffect(() => {
    if (!hasSummary || !expanded) {
      setCanExpand(false);
      return;
    }
    const el = summaryRef.current;
    if (!el) {
      setCanExpand(false);
      return;
    }
    const overflow = el.scrollHeight > SUMMARY_COLLAPSED_HEIGHT + 2;
    setCanExpand(overflow);
  }, [summaryBody, hasSummary, expanded]);

  const iconMeta = agents_icons.find((a) => a.name === card.agentName);
  const IconComponent = iconMeta?.icon;

  const hasMultipleTasks = tasks.length > 1;
  const glowRgb = showLoader
    ? hexToRgbChannels(color)
    : hexToRgbChannels(COMPLETED_GLOW);
  const glowClass = showLoader
    ? "agent-run-card--running"
    : "agent-run-card--completed";

  return (
    <div
      className={`agent-run-card ${glowClass}`}
      style={{
        borderRadius: 18,
        border: "1px solid rgba(255,255,255,0.08)",
        background:
          "linear-gradient(145deg, rgba(10,12,18,0.92), rgba(6,8,14,0.9))",
        boxShadow: "0 14px 32px rgba(0,0,0,0.35)",
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        overflow: "visible",
        "--glow-rgb": glowRgb,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = color;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.05)";
      }}
    >
      <div
        style={{
          padding: "18px",
          display: "flex",
          alignItems: "center",
          gap: 12,
          cursor: "pointer",
        }}
        onClick={onToggle}
      >
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${color}25, rgba(255,255,255,0.08))`,
            color: "#f6f7fb",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: 12,
            border: `1px solid ${color}55`,
          }}
        >
          {index + 1}
        </div>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            backgroundColor: "rgba(255,255,255,0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color,
          }}
        >
          {IconComponent ? (
            <IconComponent size={20} />
          ) : (
            card.agentName
              .split(" ")
              .map((word) => word[0])
              .join("")
              .slice(0, 3)
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 700,
                color: "#f6f7fb",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {card.agentName}
            </h3>
            {card.status === "running" ? (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  color,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {/* <Loader2 size={14} style={{ color }} /> */}
                Running
              </span>
            ) : (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  color: "#c7cbd6",
                  fontSize: 12,
                }}
              >
                {/* <CheckCircle2 size={14} color={color} /> */}
                Completed in {durationLabel}
              </span>
            )}
          </div>
          <div
            style={{
              display: "flex",
              gap: 10,
              fontSize: 12,
              color: "#9ca3af",
              marginTop: 4,
            }}
          >
            {/* <span>Duration: {durationLabel}</span> */}
          </div>
        </div>
        <button
          style={{
            border: "none",
            background: "transparent",
            color: "#9ca3af",
            padding: 6,
            borderRadius: 10,
          }}
        >
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
      </div>
      {expanded && (
        <div
          style={{
            borderTop: "1px solid rgba(255,255,255,0.06)",
            padding: "0 20px 18px 20px",
            display: "grid",
            gap: 12,
          }}
        >
          {hasMultipleTasks && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              {tasks.map((task, idx) => {
                const selected = task.taskKey === activeTask.taskKey;
                return (
                  <button
                    key={task.taskKey || `${card.key}-task-${idx}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveTaskKey(task.taskKey);
                    }}
                    style={{
                      borderRadius: 999,
                      border: selected
                        ? `1px solid ${color}`
                        : "1px solid rgba(255,255,255,0.12)",
                      background: selected
                        ? `linear-gradient(135deg, ${color}33, rgba(255,255,255,0.08))`
                        : "rgba(255,255,255,0.02)",
                      color: selected ? "#f3f4f6" : "#9ca3af",
                      fontSize: 12,
                      fontWeight: 600,
                      padding: "6px 12px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <span>{task.label || `Task ${idx + 1}`}</span>
                    <span
                      style={{
                        fontSize: 10,
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                        color: selected ? "#e5e7eb" : "#9ca3af",
                      }}
                    >
                      {task.status === "completed" ? "Done" : "Active"}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          {showLoader ? (
            <div
              style={{
                textAlign: "center",
                padding: "32px 12px",
                color: "#9ca3af",
                display: "grid",
                gap: 8,
                justifyItems: "center",
              }}
            >
              <Loader2
                size={28}
                style={{ color, animation: "spin 1s linear infinite" }}
              />
              <div style={{ fontSize: 13, fontWeight: 600, color: "#f3f4f6" }}>
                Processing agent output…
              </div>
              <div style={{ fontSize: 12 }}>
                We’ll surface the summary once the agent completes.
              </div>
            </div>
          ) : hasContent ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                padding: "4px 0",
              }}
            >
              {hasSummary && (
                <div style={{ position: "relative", margin: "6px 6px 0" }}>
                  <div
                    ref={summaryRef}
                    style={{
                      background: "rgba(255,255,255,0.02)",
                      borderRadius: 14,
                      border: "1px solid rgba(255,255,255,0.06)",
                      boxShadow: "inset 0 0 22px rgba(0,0,0,0.35)",
                      padding: "14px 18px 16px 18px",
                      color: "#e5e7eb",
                      fontSize: 13,
                      lineHeight: 1.6,
                      display: "grid",
                      gap: 12,
                      maxHeight: showAll ? "none" : SUMMARY_COLLAPSED_HEIGHT,
                      overflow: "hidden",
                    }}
                  >
                    {summaryBlocks.map((block, idx) => {
                      if (block.type === "list") {
                        return (
                          <ul
                            key={`list_${idx}`}
                            style={{
                              margin: 0,
                              paddingLeft: 18,
                              display: "grid",
                              gap: 6,
                              color: "#d1d5db",
                            }}
                          >
                            {block.items.map((item, itemIdx) => (
                              <li
                                key={`list_${idx}_${itemIdx}`}
                                style={{ lineHeight: 1.6 }}
                              >
                                {highlightContent(item, color)}
                              </li>
                            ))}
                          </ul>
                        );
                      }
                      return (
                        <p
                          key={`para_${idx}`}
                          style={{
                            margin: 0,
                          }}
                        >
                          {highlightContent(block.text, color)}
                        </p>
                      );
                    })}
                  </div>
                  {!showAll && canExpand && (
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: 70,
                        borderRadius: "0 0 12px 12px",
                        background:
                          "linear-gradient(0deg, rgba(0,0,0,0.55), transparent)",
                        pointerEvents: "none",
                      }}
                    />
                  )}
                </div>
              )}
              {hasSources && (
                <div
                  style={{
                    margin: hasSummary ? "4px 6px 0" : "6px 6px 0",
                    borderRadius: 14,
                    padding: "12px 14px",
                    border: `1px solid ${color}28`,
                    background: "rgba(255,255,255,0.02)",
                    boxShadow: "none",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      fontSize: 12,
                      letterSpacing: "0.2em",
                      textTransform: "uppercase",
                      color: "#e5e7eb",
                    }}
                  >
                    <span
                      style={{
                        padding: "4px 10px",
                        borderRadius: 999,
                        background: `rgba(255,255,255,0.08)`,
                        color: "#0b0e16",
                        fontWeight: 700,
                        letterSpacing: "0.1em",
                      }}
                    >
                      Sources
                    </span>
                    <span style={{ color: "#9ca3af", fontSize: 11 }}>
                      {sources.length} reference{sources.length > 1 ? "s" : ""}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                    }}
                  >
                    {sources.map((src) => (
                      <a
                        key={src}
                        href={src}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          textDecoration: "none",
                          fontSize: 12,
                          color,
                          background: "rgba(255,255,255,0.02)",
                          padding: "6px 10px",
                          borderRadius: 10,
                          border: `1px solid ${color}35`,
                          fontFamily: "menlo, SFMono-Regular, monospace",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          boxShadow: "none",
                        }}
                      >
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            backgroundColor: color,
                            boxShadow: "none",
                          }}
                        />
                        {formatSourceLabel(src)}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {canExpand && (
                <button
                  style={{
                    border: "none",
                    background: "transparent",
                    color: "#a5b4fc",
                    fontSize: 12,
                    fontWeight: 600,
                    textAlign: "left",
                    cursor: "pointer",
                  }}
                  onClick={() => setShowAll((prev) => !prev)}
                >
                  {showAll ? "View less" : "View more"}
                </button>
              )}
            </div>
          ) : (
            <div
              style={{
                fontSize: 13,
                color: "#9ca3af",
              }}
            >
              No summary provided for this agent.
            </div>
          )}
          <div
            style={{
              borderTop: "1px solid rgba(255,255,255,0.05)",
              paddingTop: 12,
            }}
          >
            <FileButtons files={activeTask.files || []} color={color} />
          </div>
        </div>
      )}
    </div>
  );
}

export default function AgentRunVizPane({ events = [], showHeader = true }) {
  const cards = useMemo(() => deriveAgentCards(events), [events]);
  const [expandedMap, setExpandedMap] = useState({});
  const insightAccent = "#7a8bff";

  useEffect(() => {
    setExpandedMap((prev) => {
      const next = { ...prev };
      cards.forEach((card) => {
        if (typeof next[card.key] === "undefined") {
          next[card.key] = cards[0]?.key === card.key;
        }
      });
      Object.keys(next).forEach((key) => {
        if (!cards.find((card) => card.key === key)) {
          delete next[key];
        }
      });
      return next;
    });
  }, [cards]);

  if (!cards.length) {
    return (
      <div style={{ padding: "32px 16px" }}>
        <div style={{ textAlign: "center", color: "#9ca3af", fontSize: 14 }}>
          Agent logs will appear here once tool calls start.
        </div>
      </div>
    );
  }

  const runningCount = cards.filter((c) => c.status === "running").length;
  const completedCount = cards.filter((c) => c.status === "completed").length;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {showHeader && (
        <button
          type="button"
          style={{
            padding: "20px 20px 0px 20px",
            borderBottom: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "transparent",
            border: "none",
            width: "100%",
            textAlign: "left",
            cursor: "pointer",
          }}
        >
          <div>
            <div
              style={{
                textTransform: "uppercase",
                letterSpacing: "0.15em",
                fontSize: 11,
                color: "#9ca3af",
              }}
            >
              Live Agent Runs
            </div>
            <div
              style={{
                display: "flex",
                gap: 12,
                marginTop: 6,
                fontSize: 14,
                fontWeight: 500,
                color: "#e5e7eb",
              }}
            >
              <span>{completedCount}/{cards.length} completed reports</span>
              <span style={{ color: "#c3c7d1" }}>{runningCount} running</span>
            </div>
          </div>
        </button>
      )}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: showHeader ? "10px 20px 24px 20px" : "10px 20px 24px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {cards.map((card, idx) => (
          <AgentCard
            key={card.key}
            card={card}
            index={idx}
            expanded={Boolean(expandedMap[card.key])}
            onToggle={() =>
              setExpandedMap((prev) => ({
                ...prev,
                [card.key]: !prev[card.key],
              }))
            }
          />
        ))}
      </div>
    </div>
  );
}
